import React, { useMemo, useState } from 'react';
import DateCalendar from '../../../../components/calendars/DateCalendar/DateCalendar';
import { exportUserStatistics } from '../../../../services/folder/form-avaliable';
import { localToUTCStringForDateRange } from '../../../../utility/dates/localToUTCForDateRange';
import SelectFormsModal from '../../export-user-submissions/SelectFormsModal';
import './styles.scss';

const triggerBrowserDownload = (blob, fileName) => {
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = fileName;
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(url);
};

const ExportStatisticsPanel = ({ isOpen, onClose, userID }) => {
  const [submissionDateRange, setSubmissionDateRange] = useState([null, null]);
  const [applyDatesToInitiation, setApplyDatesToInitiation] = useState(false);
  const [selectedForms, setSelectedForms] = useState([]);
  const [isSelectFormsModalOpen, setIsSelectFormsModalOpen] = useState(false);
  const [isGenerating, setIsGenerating] = useState(false);

  const [submissionDateAfter, submissionDateBefore] = submissionDateRange;

  const handleDateChange = (date) => {
    setSubmissionDateRange([date[0], date[1]]);
  };

  const selectedFormsInputValue = useMemo(() => selectedForms.map(({ name }) => name).join(', '), [selectedForms]);

  const handleFormChange = (key, value) => {
    if (key === 'forms') {
      setSelectedForms(value);
    }
  };

  const handleClose = () => {
    if (isGenerating) return;
    setSubmissionDateRange([null, null]);
    setApplyDatesToInitiation(false);
    setSelectedForms([]);
    onClose();
  };

  const handleGenerate = async () => {
    if (isGenerating || !selectedForms.length) return;

    const body = {
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      forms_ids: selectedForms.map((form) => form.id),
    };

    if (submissionDateAfter) {
      body.submission_date_after = localToUTCStringForDateRange(submissionDateAfter, 'start');
    }
    if (submissionDateBefore) {
      body.submission_date_before = localToUTCStringForDateRange(submissionDateBefore, 'end');
    }

    if (applyDatesToInitiation) {
      if (submissionDateAfter) {
        body.initiation_date_after = localToUTCStringForDateRange(submissionDateAfter, 'start');
      }
      if (submissionDateBefore) {
        body.initiation_date_before = localToUTCStringForDateRange(submissionDateBefore, 'end');
      }
    }

    setIsGenerating(true);
    try {
      const response = await exportUserStatistics(userID, body);
      const blob = new Blob([response.data], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      });
      triggerBrowserDownload(blob, 'Statistics.xlsx');
    } catch (e) {
      console.error(e);
    } finally {
      setIsGenerating(false);
    }
  };

  if (!isOpen) return null;

  return (
    <>
      <div className="export-statistics-panel-overlay" onClick={handleClose} />
      <div className="report-builder export-statistics-panel">
        <aside className="report-builder-aside">
          <div className="export-statistics-panel-header">
            <h2 className="title">Statistics</h2>
            <button type="button" className="close-btn" onClick={handleClose} aria-label="Close">
              <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
                <path d="M18 6L6 18M6 6L18 18" stroke="#101828" strokeWidth="1.67" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </button>
          </div>

          <div className="form-item mb-2 mt-4">
            <label className="form-label">Select forms</label>
            <div className="form-item-wrapper">
              <input
                value={selectedFormsInputValue}
                onClick={() => setIsSelectFormsModalOpen(true)}
                readOnly
                type="text"
                className="form-control"
                placeholder="Select..."
              />
              <svg className="icon-search" xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
                <path
                  fillRule="evenodd"
                  clipRule="evenodd"
                  d="M4 11C4 7.13401 7.13401 4 11 4C14.866 4 18 7.13401 18 11C18 12.8858 17.2543 14.5974 16.0417 15.8561C16.0073 15.8825 15.9743 15.9114 15.9428 15.9429C15.9113 15.9744 15.8824 16.0074 15.856 16.0418C14.5973 17.2543 12.8857 18 11 18C7.13401 18 4 14.866 4 11ZM16.6176 18.0319C15.078 19.2635 13.125 20 11 20C6.02944 20 2 15.9706 2 11C2 6.02944 6.02944 2 11 2C15.9706 2 20 6.02944 20 11C20 13.125 19.2635 15.0781 18.0319 16.6177L21.707 20.2929C22.0975 20.6834 22.0975 21.3166 21.707 21.7071C21.3165 22.0976 20.6833 22.0976 20.2928 21.7071L16.6176 18.0319Z"
                  fill="#98A2B3"
                />
              </svg>
            </div>
          </div>

          <div className="form-item mb-2 mt-2">
            <label className="form-label">Period of submission</label>
            <DateCalendar
              selectedDate={submissionDateAfter}
              startDate={submissionDateAfter}
              endDate={submissionDateBefore}
              handleChange={handleDateChange}
              selectsRange
              isClearable
            />
          </div>

          <div className={'form-item mb-6 mt-2'}>
            <div className="form-control-checkbox d-flex cursor-pointer">
              <div>
                <input type="checkbox" onChange={(e) => setApplyDatesToInitiation(e.target.checked)} checked={applyDatesToInitiation} />
                <span className={''}></span>
              </div>
              <label className="apply-dates-label">Apply the selected dates to the &ldquo;Period of form initiation&rdquo;</label>
            </div>
          </div>

          <div className="generate-report-btn-wrapper">
            <button type="button" className="btn" onClick={handleGenerate} disabled={!selectedForms.length || isGenerating}>
              <svg className="mr-2" xmlns="http://www.w3.org/2000/svg" width="21" height="20" viewBox="0 0 21 20" fill="none">
                <path
                  d="M19.6668 3.33136V8.33136M19.6668 8.33136H14.6668M19.6668 8.33136L15.8002 4.69803C14.9045 3.80196 13.7965 3.14737 12.5795 2.79534C11.3625 2.44331 10.0761 2.40532 8.8404 2.68491C7.60472 2.9645 6.46 3.55256 5.51305 4.39421C4.56611 5.23586 3.8478 6.30368 3.42516 7.49803M1.3335 16.6647V11.6647M1.3335 11.6647H6.3335M1.3335 11.6647L5.20016 15.298C6.09579 16.1941 7.20381 16.8487 8.42084 17.2007C9.63787 17.5527 10.9242 17.5907 12.1599 17.3111C13.3956 17.0316 14.5403 16.4435 15.4873 15.6018C16.4342 14.7602 17.1525 13.6924 17.5752 12.498"
                  stroke="white"
                  strokeWidth="1.67"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
              {isGenerating ? 'Generating...' : 'Generate Report'}
            </button>
          </div>
        </aside>
      </div>

      {isSelectFormsModalOpen && (
        <SelectFormsModal
          userID={userID}
          formData={{ forms: selectedForms }}
          handleFormChange={handleFormChange}
          onClose={() => setIsSelectFormsModalOpen(false)}
        />
      )}
    </>
  );
};

export default ExportStatisticsPanel;
